import TouchApp from "@mui/icons-material/TouchApp";
import { Box, MenuItem, Stack, Typography } from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import type { Option } from "../constants";
import AddConditionButton from "../buttons/AddConditionButton";
import RaisedTrashIconButton from "../buttons/RaisedTrashIconButton";
import BaseForm from "./BaseForm";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

export interface EventListenerFormState {
	eventListenerName: string;
	targetScreenFieldIds: string[];
	targetScreenFieldNames?: string[];
	action: string;
}

export interface EventListenerFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: EventListenerFormState) => void;
	initialState?: Partial<EventListenerFormState>;
	isSaving?: boolean;
	screenLocationId?: string | number;
	getDuplicateNameError?: (name: string) => string | undefined;
}

interface TargetRow {
	id: string;
	screenFieldId: string;
}

const DEFAULT_ACTION = "click";

const isButtonScreenField = (field: { htmlFieldType?: { name?: string } }): boolean =>
	String(field.htmlFieldType?.name ?? "").toLowerCase() === "button";

const buildTargetRows = (ids: string[] | undefined): TargetRow[] => {
	if (ids?.length) {
		return ids.map((screenFieldId) => ({
			id: uuidv4(),
			screenFieldId: String(screenFieldId),
		}));
	}
	return [{ id: uuidv4(), screenFieldId: "" }];
};

const EventListenerForm = (props: EventListenerFormProps) => {
	const {
		open,
		onClose,
		onSave,
		initialState,
		isSaving = false,
		screenLocationId,
		getDuplicateNameError,
	} = props;

	const authHeader = useAuthHeader();

	const initialFormState = useMemo(
		(): { name: string; targets: TargetRow[] } => ({
			name: initialState?.eventListenerName ?? "",
			targets: buildTargetRows(initialState?.targetScreenFieldIds),
		}),
		[initialState],
	);

	const [eventListenerName, setEventListenerName] = useState(initialFormState.name);
	const [targets, setTargets] = useState<TargetRow[]>(initialFormState.targets);
	const [buttonFieldOptions, setButtonFieldOptions] = useState<Option[]>([]);
	const [fieldsLoading, setFieldsLoading] = useState(false);

	useEffect(() => {
		if (open) {
			setEventListenerName(initialFormState.name);
			setTargets(initialFormState.targets);
		}
	}, [initialFormState, open]);

	useEffect(() => {
		if (!open || screenLocationId == null || screenLocationId === "") {
			setButtonFieldOptions([]);
			return;
		}

		let cancelled = false;
		setFieldsLoading(true);

		const fetchButtonFields = async () => {
			try {
				const res = await axios.get(apiRoutes.getScreenFields(screenLocationId), {
					headers: authHeader,
				});
				if (cancelled) return;
				const options: Option[] = (res.data ?? [])
					.filter(isButtonScreenField)
					.map((field: { id: number; name: string }) => ({
						label: field.name,
						value: String(field.id),
					}));
				setButtonFieldOptions(options);
			} catch (error) {
				console.error("Error fetching screen fields for event listener:", error);
				if (!cancelled) setButtonFieldOptions([]);
			} finally {
				if (!cancelled) setFieldsLoading(false);
			}
		};

		fetchButtonFields();
		return () => {
			cancelled = true;
		};
	}, [open, screenLocationId, authHeader]);

	const duplicateNameError = useMemo(
		() => getDuplicateNameError?.(eventListenerName),
		[getDuplicateNameError, eventListenerName],
	);

	const allSelectedIds = useMemo(
		() => new Set(targets.map((t) => t.screenFieldId).filter(Boolean)),
		[targets],
	);

	const optionsForRow = (row: TargetRow): Option[] =>
		buttonFieldOptions.filter(
			(opt) => opt.value === row.screenFieldId || !allSelectedIds.has(opt.value),
		);

	const isSaveDisabled =
		!eventListenerName.trim() ||
		Boolean(duplicateNameError) ||
		targets.some((t) => !t.screenFieldId) ||
		(fieldsLoading && buttonFieldOptions.length === 0);

	const handleSave = () => {
		const targetScreenFieldIds = targets.map((t) => t.screenFieldId).filter(Boolean);
		const targetScreenFieldNames = targetScreenFieldIds.map((id) => {
			const match = buttonFieldOptions.find((o) => o.value === id);
			return match?.label ?? "";
		});

		onSave({
			eventListenerName: eventListenerName.trim(),
			targetScreenFieldIds,
			targetScreenFieldNames,
			action: DEFAULT_ACTION,
		});
	};

	const handleAddTarget = () => {
		setTargets((prev) => [...prev, { id: uuidv4(), screenFieldId: "" }]);
	};

	const handleRemoveTarget = (rowId: string) => {
		setTargets((prev) => (prev.length > 1 ? prev.filter((t) => t.id !== rowId) : prev));
	};

	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Event Listener"
			icon={<TouchApp />}
			iconColor="#3F77E8"
			gradient="linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Event Listener"
		>
			<Stack spacing={3}>
				<CanvasFormTextField
					fullWidth
					label="Name"
					value={eventListenerName}
					error={Boolean(duplicateNameError)}
					helperText={duplicateNameError ?? ""}
					onChange={(event) => setEventListenerName(event.target.value)}
					width="100%"
				/>

				<Stack spacing={1.5}>
					<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
						Targets
					</Typography>
					{targets.map((row, index) => {
						const rowOptions = optionsForRow(row);
						const selectedLabel =
							rowOptions.find((o) => o.value === row.screenFieldId)?.label ?? "";

						return (
							<Stack key={row.id} direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
								<CanvasFormTextField
									select
									fullWidth
									label={index === 0 ? "Target" : `Target ${index + 1}`}
									value={selectedLabel}
									disabled={fieldsLoading || rowOptions.length === 0}
									helperText={
										!fieldsLoading && rowOptions.length === 0
											? "No button fields at this location"
											: ""
									}
									onChange={(event) => {
										const label = event.target.value;
										const selected = rowOptions.find((o) => o.label === label);
										setTargets((prev) =>
											prev.map((t) =>
												t.id === row.id
													? { ...t, screenFieldId: selected?.value ?? "" }
													: t,
											),
										);
									}}
									width="100%"
								>
									{rowOptions.map((opt) => (
										<MenuItem key={opt.value} value={opt.label}>
											{opt.label}
										</MenuItem>
									))}
								</CanvasFormTextField>
								{targets.length > 1 && (
									<Box sx={{ pt: 0.25 }}>
										<RaisedTrashIconButton
											aria-label="Remove target"
											onClick={() => handleRemoveTarget(row.id)}
										/>
									</Box>
								)}
							</Stack>
						);
					})}
					<AddConditionButton label="Add Target" onClick={handleAddTarget} />
				</Stack>

				<CanvasFormTextField
					select
					fullWidth
					label="Action"
					value="Click"
					disabled
					width="100%"
				>
					<MenuItem value="Click">Click</MenuItem>
				</CanvasFormTextField>
			</Stack>
		</BaseForm>
	);
};

export default EventListenerForm;
